import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useCampaignTotpStepUp } from '@/contexts/CampaignTotpStepUpContext'
import { useAuth } from '@/hooks/useAuth'
import { canManageInstitutionCampaigns } from '@/lib/campaign-totp-step-up'

type CampaignTotpStepUpGateProps = {
  canAccess?: boolean
  children: ReactNode
  fallbackTo: string
  forceRefresh?: boolean
}

export function CampaignTotpStepUpGate({
  canAccess,
  children,
  fallbackTo,
  forceRefresh = false,
}: CampaignTotpStepUpGateProps) {
  const { guard, isCollaborator } = useAuth()
  const allowed = canAccess ?? canManageInstitutionCampaigns(guard, Boolean(isCollaborator))
  const { ensureElevated } = useCampaignTotpStepUp()
  const [elevatedState, setElevatedState] = useState<'checking' | 'allowed' | 'blocked'>(
    'checking'
  )

  useEffect(() => {
    if (!allowed) {
      return
    }

    let cancelled = false

    void ensureElevated(forceRefresh).then((nextAllowed) => {
      if (cancelled) return
      setElevatedState(nextAllowed ? 'allowed' : 'blocked')
    })

    return () => {
      cancelled = true
    }
  }, [allowed, ensureElevated, forceRefresh])

  if (!allowed || elevatedState === 'blocked') {
    return <Navigate to={fallbackTo} replace />
  }

  if (elevatedState !== 'allowed') {
    return (
      <div role="status" aria-live="polite">
        Confirmando autenticação em duas etapas...
      </div>
    )
  }

  return <>{children}</>
}
